import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import UserDirectory from './components/UserDirectory';
import CreateUserModal from './components/CreateUserModal';
import UserAnalytics from './components/UserAnalytics';

const UserManagementDashboard = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, loading: authLoading, profileLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [analytics, setAnalytics] = useState({
    totalStaff: 0,
    totalMembers: 0,
    activeUsers: 0,
    pendingAccounts: 0
  });

  useEffect(() => {
    // Wait for both auth and profile to load before checking permissions
    if (!authLoading && !profileLoading) {
      if (!user || !isGymOwner) {
        navigate('/login');
      }
    }
  }, [user, isGymOwner, authLoading, profileLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id) {
      loadUsers();
    }
  }, [currentTenant]);

  const loadUsers = async () => {
    setLoading(true);
    setError('');

    try {
      const { data: usersData, error: usersError } = await supabase?.from('tenant_members')?.select('*, user_profiles(*)')?.eq('tenant_id', currentTenant?.id)?.order('joined_at', { ascending: false });

      if (usersError) throw usersError;

      setUsers(usersData || []);

      // Calculate analytics
      const staff = usersData?.filter(u => ['staff', 'trainer', 'gym_owner']?.includes(u?.role)) || [];
      const members = usersData?.filter(u => u?.role === 'member') || [];
      const active = usersData?.filter(u => u?.is_active) || [];

      setAnalytics({
        totalStaff: staff?.length,
        totalMembers: members?.length,
        activeUsers: active?.length,
        pendingAccounts: usersData?.filter(u => !u?.is_active)?.length || 0
      });
    } catch (err) {
      setError(err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setShowCreateModal(true);
  };

  const handleUserCreated = () => {
    setShowCreateModal(false);
    loadUsers();
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading user management...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-[1440px] px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                User Management Dashboard
              </h1>
              <p className="text-muted-foreground">
                Create and manage gym staff and member accounts with role-based permissions
              </p>
            </div>
            <Button onClick={handleCreateUser} className="flex items-center gap-2">
              <Icon name="UserPlus" size={20} />
              Create User
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-3">
            <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
            <p className="text-destructive">{error}</p>
          </div>
        )}

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-card rounded-xl shadow-md p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <Icon name="Users" size={24} color="var(--color-primary)" />
              <span className="text-2xl font-bold text-foreground">{analytics?.totalStaff}</span>
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">Total Staff</h3>
            <p className="text-xs text-muted-foreground mt-1">Trainers, staff, managers</p>
          </div>

          <div className="bg-card rounded-xl shadow-md p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <Icon name="UserCheck" size={24} color="var(--color-success)" />
              <span className="text-2xl font-bold text-foreground">{analytics?.totalMembers}</span>
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">Total Members</h3>
            <p className="text-xs text-muted-foreground mt-1">Active gym members</p>
          </div>

          <div className="bg-card rounded-xl shadow-md p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <Icon name="Activity" size={24} color="var(--color-accent)" />
              <span className="text-2xl font-bold text-foreground">{analytics?.activeUsers}</span>
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">Active Users</h3>
            <p className="text-xs text-muted-foreground mt-1">Currently active accounts</p>
          </div>

          <div className="bg-card rounded-xl shadow-md p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <Icon name="Clock" size={24} color="var(--color-warning)" />
              <span className="text-2xl font-bold text-foreground">{analytics?.pendingAccounts}</span>
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">Pending Accounts</h3>
            <p className="text-xs text-muted-foreground mt-1">Inactive or suspended</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Directory - Takes 2 columns */}
          <div className="lg:col-span-2">
            <UserDirectory users={users} onRefresh={loadUsers} />
          </div>

          {/* User Analytics - Takes 1 column */}
          <div className="lg:col-span-1">
            <UserAnalytics users={users} />
          </div>
        </div>
      </main>
      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onUserCreated={handleUserCreated}
          tenantId={currentTenant?.id}
        />
      )}
      <MobileBottomNav />
    </div>
  );
};

export default UserManagementDashboard;